import { inject, nextTick, onBeforeUnmount, provide, ref } from "vue";

/**
 * Painel ancorado a um gatilho e **fora do fluxo**: quem usa teleporta o painel
 * para o body e aplica `panelStyle` nele (ou usa o componente AnchoredPanel,
 * que já faz as duas coisas).
 *
 * É a regra do design system para popover dentro de área com overflow. Um
 * painel em `position: absolute` dentro de um contêiner com `overflow` diferente
 * de `visible` é recortado - e o próprio menu canônico (`.row-dropdown-menu`,
 * `overflow-y: auto` para rolar quando é longo) é um desses contêineres. Por
 * isso um sub-painel aberto de dentro de um menu some por inteiro: o clique
 * funciona, o estado muda, e nada aparece. `position: fixed` + medida a partir
 * do gatilho resolve.
 *
 * Uso:
 *   const { isOpen, triggerRef, panelRef, panelStyle, toggle, close } =
 *     useAnchoredPanel({ align: "right" });
 *
 * Opções:
 * - `placement`: "below" (padrão) abre abaixo do gatilho, ou acima se não
 *   couber; "side" abre ao lado (sub-painel de um item de menu), alinhado pelo
 *   topo do gatilho e empurrado para dentro da tela se passar da borda.
 * - `align`: com "below", por qual borda o painel se alinha ao gatilho -
 *   "left" (padrão) ou "right" (gatilho no canto direito da tela). Com "side",
 *   o lado preferido - "left" ou "right" (padrão); troca de lado se não couber.
 * - `gap`: distância entre gatilho e painel, em px.
 * - `onClose`: chamado quando o painel se fecha sozinho (clique fora, Esc) -
 *   para quem controla a abertura por prop e precisa emitir `close`.
 *
 * Painéis aninhados: cada painel avisa o painel acima dele (pela árvore de
 * componentes, que sobrevive ao teleporte) de que existe. Assim um clique no
 * sub-painel não conta como "fora" do painel pai, e o Esc fecha só o mais
 * interno. Um contêiner que tenha o próprio "fechar ao clicar fora" e hospede
 * painéis ancorados usa `useAnchoredPanelScope()` para fazer a mesma pergunta.
 */
const SCOPE_KEY = Symbol("anchored-panel-scope");

const VIEWPORT_MARGIN = 8;

/**
 * Escopo de painéis ancorados para um contêiner que não é ele mesmo um painel
 * ancorado (ex.: um dropdown antigo em `position: absolute` com o próprio
 * click-outside). Retorna `contains(target)`: verdadeiro se o alvo está dentro
 * de algum painel aberto a partir deste contêiner - teleportado, portanto fora
 * dele no DOM.
 */
export function useAnchoredPanelScope() {
  const children = new Set();
  provide(SCOPE_KEY, {
    add: (child) => children.add(child),
    remove: (child) => children.delete(child),
  });
  return {
    contains: (target) => [...children].some((c) => c.contains(target)),
    hasOpenChild: () => [...children].some((c) => c.isOpen()),
  };
}

export function useAnchoredPanel({
  align,
  gap = 4,
  placement = "below",
  onClose,
} = {}) {
  const isOpen = ref(false);
  const triggerRef = ref(null);
  const panelRef = ref(null);
  const panelStyle = ref({});

  // Registro no painel/escopo de cima e escopo próprio para os de baixo.
  const parent = inject(SCOPE_KEY, null);
  const scope = useAnchoredPanelScope();
  const self = {
    isOpen: () => isOpen.value,
    contains: (target) =>
      !!triggerRef.value?.contains(target) ||
      !!panelRef.value?.contains(target) ||
      scope.contains(target),
  };
  parent?.add(self);

  function placeBelow(r, w, h) {
    const espacoAbaixo = window.innerHeight - r.bottom;
    // Só sobe quando não cabe abaixo e cabe acima.
    const abreParaCima = h > espacoAbaixo && r.top > espacoAbaixo;
    return {
      ...((align ?? "left") === "right"
        ? { right: `${Math.round(window.innerWidth - r.right)}px` }
        : { left: `${Math.round(r.left)}px` }),
      ...(abreParaCima
        ? { bottom: `${Math.round(window.innerHeight - r.top + gap)}px` }
        : { top: `${Math.round(r.bottom + gap)}px` }),
    };
  }

  function placeSide(r, w, h) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const cabeEsquerda = r.left - gap - w >= VIEWPORT_MARGIN;
    const cabeDireita = r.right + gap + w <= vw - VIEWPORT_MARGIN;
    const prefereEsquerda = (align ?? "right") === "left";
    const vaiEsquerda = prefereEsquerda ? cabeEsquerda || !cabeDireita : !cabeDireita && cabeEsquerda;
    let left = vaiEsquerda ? r.left - gap - w : r.right + gap;
    left = Math.min(Math.max(left, VIEWPORT_MARGIN), vw - VIEWPORT_MARGIN - w);
    // Topo do painel no topo do gatilho; se passar do rodapé, sobe o quanto
    // precisar, sem passar do topo da tela.
    const top = Math.max(VIEWPORT_MARGIN, Math.min(r.top, vh - VIEWPORT_MARGIN - h));
    return { left: `${Math.round(left)}px`, top: `${Math.round(top)}px` };
  }

  // O painel acompanha o gatilho: medida na hora e refeita a cada rolagem
  // (inclusive a de contêineres internos, daí o capture) e redimensionamento.
  function place() {
    const el = triggerRef.value;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const w = panelRef.value?.offsetWidth ?? 0;
    const h = panelRef.value?.offsetHeight ?? 0;
    panelStyle.value = placement === "side" ? placeSide(r, w, h) : placeBelow(r, w, h);
  }

  async function open() {
    if (isOpen.value) return;
    isOpen.value = true;
    // Mede com o tamanho real já renderizado - é ele que decide o lado.
    await nextTick();
    place();
    listen(true);
  }

  function close() {
    if (!isOpen.value) return;
    isOpen.value = false;
    listen(false);
  }

  function toggle() {
    return isOpen.value ? close() : open();
  }

  function closeByUser() {
    close();
    onClose?.();
  }

  function onOutside(event) {
    // O próprio gatilho alterna no click; fechar aqui o reabriria em seguida.
    // Sub-painéis teleportados também contam como "dentro".
    if (self.contains(event.target)) return;
    closeByUser();
  }

  // Enquanto o painel está aberto, o Esc é dele - mesmo com o foco em outro
  // lugar. Captura na janela para chegar antes de quem estiver atrás e para ali.
  // Com um sub-painel aberto, o Esc é do sub-painel: fecha um nível por vez.
  // Pai e filho escutam no mesmo alvo (window); `stopImmediatePropagation`
  // impede o pai de fechar também quando o filho foi registrado antes dele.
  function onWindowEscape(event) {
    if (event.key !== "Escape" || !isOpen.value || scope.hasOpenChild()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    closeByUser();
    triggerRef.value?.focus?.();
  }

  function listen(on) {
    const fn = on ? window.addEventListener : window.removeEventListener;
    fn("scroll", place, true);
    fn("resize", place);
    fn("mousedown", onOutside, true);
    fn("keydown", onWindowEscape, true);
  }

  onBeforeUnmount(() => {
    listen(false);
    parent?.remove(self);
  });

  return { isOpen, triggerRef, panelRef, panelStyle, open, close, toggle, place };
}
